import React, { useEffect, useState } from 'react';
import { Label } from 'reactstrap';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';
import BackButton from '../../custom/BackButton';
import { createdUpdatedInfo, getTsDate, notify } from '../../../utils';
import ActionIcon from '../../custom/ActionIcon';
import { TrainingModule } from '../../../globals/interfaces';
import { FormGroup } from 'reactstrap';
import { Input } from 'reactstrap';
import SliderWithInput from '../../SliderWithInput';
import apiClient from '../../../apiClient';
import useUser from '../../../customHooks/useUser';
import _ from 'lodash';

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    trainingModule: TrainingModule;
    setTrainingModules: React.Dispatch<React.SetStateAction<Array<TrainingModule>>>;
}

export default function ModalManageTrainingModule ({
    modal,
    setModal,
    trainingModule,
    setTrainingModules
}: Props) {

    const { user } = useUser();
    const [f_pass_percent, set_f_pass_percent] = useState('');
    const [i_training_frequency_months, set_i_training_frequency_months] = useState('0');
    const [i_valid_hours, set_i_valid_hours] = useState('24');

    useEffect(() => {
        if (!trainingModule) return;
        set_f_pass_percent(String((trainingModule.f_pass_percent || 0) * 100));
        set_i_training_frequency_months(String(trainingModule.i_training_frequency_months || ''));
        set_i_valid_hours(String(trainingModule.i_valid_hours || '24'));
    }, [trainingModule]);

    const toggle = () => setModal(!modal);

    const updateTrainingModule = async () => {
        if (!trainingModule || !f_pass_percent) return;

        try {
            const updateBody: Partial<TrainingModule> = {
                f_pass_percent: Number(f_pass_percent) / 100,
                i_training_frequency_months: Number(i_training_frequency_months),
                s_modified_by: user.s_email,
                t_modified: getTsDate(),
                i_valid_hours: Number(i_valid_hours),
                id: trainingModule.id
            };
    
            await apiClient.put(`/updateTrainingModule`, updateBody);
    
            setTrainingModules(prev => {
                const copy = _.cloneDeep(prev);
                const updateIndex = copy.findIndex(item => item.id === trainingModule.id);
                const updateItem = copy[updateIndex];
                for (const key in updateBody) {
                    updateItem[key] = updateBody[key];
                }
                return copy;
            });

            toggle();
            notify('Updated Module');
        } catch (err) {
            alert(`Failed to update, ${err}`);
        }
    }

    return (
        <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>Manage Training Module</h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <FormGroup>
                    <SliderWithInput 
                        label={'Pass percent (%)'}
                        unit={'%'}
                        value={f_pass_percent}
                        setValue={set_f_pass_percent}
                    />
                </FormGroup>
                <FormGroup>
                    <Label>Expiration Time in HOURS</Label>
                    <FlexContainer>
                        <Input 
                            type={'number'} 
                            value={i_valid_hours}
                            onChange={e => set_i_valid_hours(e.target.value)}
                            style={{ width: '50px' }}
                        />
                        <Label>Hours</Label>
                    </FlexContainer>
                </FormGroup>
                <FormGroup>
                    <Label>Recurrence in MONTHS</Label>
                    <FlexContainer>
                        <Input 
                            type={'number'} 
                            value={i_training_frequency_months}
                            onChange={e => set_i_training_frequency_months(e.target.value)}
                            style={{ width: '50px' }}
                        />
                        <Label>Months</Label>
                    </FlexContainer>
                </FormGroup>
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <Label>
                        {createdUpdatedInfo(trainingModule)}
                    </Label>
                    <FooterButtonsContainer>
                        <ActionIcon 
                            type={'save'} 
                            onClick={updateTrainingModule}
                            disabled={!f_pass_percent || Number(f_pass_percent) === 0 || Number(i_valid_hours) === 0} 
                        />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </Modal>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{update?: boolean}>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;

const FlexContainer = styled.div`
    display: flex;
    align-items: baseline;
    gap: 2px;
`;
